import {
    normalizeRecapShareId,
    normalizeRecapSourceGameId,
    normalizeRecapTemplateId,
    normalizeRecapVariation,
} from '~/utils/recapReferral'

export type ReferralContext = {
    shareId: string
    origin: 'recap'
    templateId?: string
    sourceGameId?: string
    variation?: string
}

const REFERRAL_STORAGE_KEY = 'vibe_referral_context'

const normalizeReferralContext = (value: unknown): ReferralContext | null => {
    if (!value || typeof value !== 'object') return null

    const record = value as Record<string, unknown>
    const shareId = normalizeRecapShareId(record.shareId)
    if (!shareId) return null

    return {
        shareId,
        origin: 'recap',
        templateId: normalizeRecapTemplateId(record.templateId),
        sourceGameId: normalizeRecapSourceGameId(record.sourceGameId),
        variation: normalizeRecapVariation(record.variation),
    }
}

export const useReferralAttribution = () => {
    const referralContext = useState<ReferralContext | null>('referralContext', () => null)
    const hasHydratedReferral = useState<boolean>('hasHydratedReferral', () => false)

    const persistReferralContext = (value: ReferralContext | null) => {
        if (import.meta.server) return

        if (!value) {
            sessionStorage.removeItem(REFERRAL_STORAGE_KEY)
            return
        }

        sessionStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(value))
    }

    const hydrateReferralContext = () => {
        if (import.meta.server || hasHydratedReferral.value) return
        hasHydratedReferral.value = true

        const storedValue = sessionStorage.getItem(REFERRAL_STORAGE_KEY)
        if (!storedValue) return

        try {
            referralContext.value = normalizeReferralContext(JSON.parse(storedValue))
        } catch {
            sessionStorage.removeItem(REFERRAL_STORAGE_KEY)
            referralContext.value = null
        }
    }

    const setReferralContext = (value: ReferralContext | null) => {
        const normalized = normalizeReferralContext(value)
        referralContext.value = normalized
        persistReferralContext(normalized)
    }

    const getReferralContext = () => {
        hydrateReferralContext()
        return referralContext.value
    }

    const clearReferralContext = () => {
        referralContext.value = null
        persistReferralContext(null)
    }

    return {
        clearReferralContext,
        getReferralContext,
        hydrateReferralContext,
        referralContext,
        setReferralContext,
    }
}
